import { Checkbox, Form, message, Modal, Radio } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Subscription } from 'rxjs';
import currencies from '../../constants/currencies';
import { JobStatus } from '../../constants/job-status';
import { NoticePeriod } from '../../constants/notice-period';
import { SalaryFrequency } from '../../constants/salary';
import candidatesService from '../../services/candidates.service';
import jobsService from '../../services/jobs.service';
import { ITechnicalReport } from '../../types';
import { CandidateType } from '../../types/candidate-type';
import configService from '../../utils/config';
import { notifyError } from '../../utils/notification';
import { objToSearchFilter } from '../../utils/obj-to-search-filter';
import { cleanUndefined } from '../../utils/objects';
import AddReportFormModal from '../add-report-form-modal/add-report-form-modal';
import { PDFViewer } from '../pdf-viewer-modal/pdf-viewer-modal';
import { TASelect } from '../ta-select';
import { TAInputNumber } from '../widgets';

export const RecommendCandidateModal = props => {
	const [form] = Form.useForm();
	const [jobs, setJobs] = useState<any>([]);
	const [candidates, setCandidates] = useState<any>([]);
	const [jobsFilter, setJobsFilter] = useState<any>();
	const [loading, setLoading] = useState(false);
	const [technicalReports, setTechnicalReports] = useState<ITechnicalReport[] | []>([]);
	const [selectedCandidate, setSelectedCandidate] = useState<any>();
	const [selectedReport, setSelectedReport] = useState<ITechnicalReport>();
	const [newReportModalVisible, setNewReportModalVisible] = useState<boolean>(false);
	const [viewReportModalVisible, setViewReportModalVisible] = useState<boolean>(false);
	const [changedFields, setChangedFields] = useState({
		currentSalary: false,
		expectedSalary: false,
		noticePeriod: false
	});

	const [candidateType, setCandidateType] = useState(CandidateType.TALENTS_ARENA);

	const searchCandidates$ = useMemo(
		() =>
			candidatesService.getPaginatedListFetcher({
				debounce: 700,
				distinct: true,
				fetcher: ({ filter }) =>
					candidatesService.getCandidates({
						filter: objToSearchFilter(filter),
						pageSize: 50
					})
			}),
		[]
	);
	const searchJobs$ = useMemo(
		() =>
			candidatesService.getPaginatedListFetcher({
				fetcher: ({ filter }) =>
					jobsService.getJobs({
						filter: objToSearchFilter(filter),
						pageSize: 50
						// sorters: state.sortParams
					}),
				debounce: 700,
				distinct: true,
				beforeFetch: ({ filter }) => {
					setJobsFilter({ ...filter });
					setLoading(true);
					setJobs([]);
				}
			}),
		[]
	);
	const subscriptions = useMemo(() => new Subscription(), []);
	const searchCandidates = val => searchCandidates$.next({ filter: val, pageSize: 50 });
	const searchJobs = val => searchJobs$.next({ filter: val, pageSize: 50 });

	const mountLogic = useCallback(async () => {
		if (props.job) {
			try {
				subscriptions.add(
					searchCandidates$.subscribe(({ data: candidates }) => {
						setCandidates(candidates);
						setLoading(false);
					})
				);
			} catch (err) {
				notifyError('Error while fetching data, Please try again later.');
			}
			searchCandidates({ candidateType });
		}
		if (props.candidate) {
			try {
				subscriptions.add(
					searchJobs$.subscribe(({ data }) => {
						setLoading(false);
						setJobs(data);
					})
				);
			} catch (err) {
				notifyError('Error while fetching data, Please try again later.');
			}
			searchJobs({ status: JobStatus[0] });
			const defaultReport = props.candidate.technicalReport
				? { name: 'Default Report', url: props.candidate.technicalReport }
				: null;
			let technicalReports: ITechnicalReport[] = [];
			if (props.candidate.technicalReport && defaultReport) technicalReports.push(defaultReport);
			if (props.candidate.customTechnicalReports?.length)
				technicalReports = [...technicalReports, ...props.candidate.customTechnicalReports];
			setSelectedCandidate(props.candidate);
			setTechnicalReports(technicalReports);
			setSelectedReport(
				props.application?.technicalReports?.length ? props.application.technicalReports[0] : defaultReport
			);
		}
	}, []);

	useEffect(() => {
		mountLogic();
		return () => subscriptions.unsubscribe();
	}, []);

	const onCancel = () => {
		props.onCancel && props.onCancel();
	};

	const newReportHandler = e => {
		e.preventDefault();
		setNewReportModalVisible(true);
	};

	const viewReportHandler = e => {
		e.preventDefault();
		setViewReportModalVisible(true);
	};

	const addReportHandler = response => {
		form.setFieldsValue({ technicalReport: response.url });
		setTechnicalReports([...technicalReports, response]);
		setSelectedReport(response);
	};

	const setRecommendedCandidate = val => {
		const candidate = candidates.find(c => c.id === val);
		form.setFieldsValue({ candidate: candidate.id });
		form.resetFields(['technicalReport']);
		const defaultReport = candidate?.technicalReport
			? { name: 'Default Report', url: candidate.technicalReport }
			: null;

		let technicalReports: ITechnicalReport[] = [];
		if (candidate?.technicalReport && defaultReport) technicalReports.push(defaultReport);
		if (candidate?.customTechnicalReports?.length)
			technicalReports = [...technicalReports, ...candidate.customTechnicalReports];
		setSelectedCandidate(candidate);
		setTechnicalReports(technicalReports);
		setSelectedReport(defaultReport || undefined);
		form.setFieldsValue({
			technicalReport: defaultReport?.url,
			currentSalary: candidate.currentSalary,
			expectedSalary: candidate.minimumSalary,
			noticePeriod: candidate.noticePeriod
		});
	};

	const onFinish = async values => {
		if (props.candidate) values.candidate = props.candidate.id;
		if (props.job) values.job = props.job.id;
		if (values.technicalReport) values.technicalReport = selectedReport;
		else values.technicalReport = undefined;
		const candidate = props.candidate || selectedCandidate;
		const job = props.job || jobs.find(job => job.id === values.job);
		let data;
		if (props.application) {
			const applicationPayload = cleanUndefined({
				currentSalary: changedFields.currentSalary ? values.currentSalary : undefined,
				expectedSalary: changedFields.expectedSalary ? values.expectedSalary : undefined,
				noticePeriod: changedFields.noticePeriod ? values.noticePeriod : undefined,
				currentSalaryVisible: values.currentSalaryVisible,
				expectedSalaryVisible: values.expectedSalaryVisible,
				technicalReports: values.technicalReport ? [values.technicalReport] : undefined,
				noticePeriodVisible: values.noticePeriodVisible
				// cvDownloadable: values.cvDownloadable
			});
			try {
				const response: any = await jobsService.editJobApplication(props.application.id, applicationPayload);
				if (!response.success) throw new Error('Something went wrong');
				props.onEditSuccess({ ...props.application, ...applicationPayload });
			} catch (e) {
				form.resetFields();
				props.onError();
			}
		} else {
			try {
				data = await jobsService.recommendCandidate(
					values.job,
					cleanUndefined({
						id: values.candidate,
						currentSalary: values.currentSalary || undefined,
						expectedSalary: values.expectedSalary || undefined,
						noticePeriod: values.noticePeriod || undefined,
						technicalReport: values.technicalReport,
						currentSalaryVisible: values.currentSalaryVisible,
						expectedSalaryVisible: values.expectedSalaryVisible,
						noticePeriodVisible: values.noticePeriodVisible,
						// cvDownloadable: values.cvDownloadable,
						forceApply: props.forceApply
					})
				);
				if (!data || !data.accepted?.length)
					throw new Error('Error while fetching data, Please try again later.');
			} catch (e) {
				console.error(e);
				props.onError(candidate);
				return;
			}
		}
		setSelectedCandidate(null);
		setTechnicalReports([]);
		setNewReportModalVisible(false);
		setViewReportModalVisible(false);
		setJobs([]);
		form.resetFields();
		props.onSuccess && props.onSuccess(candidate, job);
	};
	return (
		<Modal
			title={
				props.title
					? props.title
					: props.job && props.company
					? `Recommend Candidate for ${props.job.title} at ${props.company.name}`
					: props.candidate
					? `Recommend ${props.candidate.firstName} ${props.candidate.lastName} for a job`
					: 'Recommend Candidate'
			}
			visible={props.visible}
			onOk={form.submit}
			onCancel={onCancel}
			okText={props.application ? 'Update' : 'Recommend'}
			className="ta ta-modal"
			confirmLoading={loading}
		>
			<Form
				layout="vertical"
				className="ta-form"
				form={form}
				scrollToFirstError
				onFinish={onFinish}
				onFieldsChange={formChangedFields => {
					setChangedFields({
						...changedFields,
						...formChangedFields
							.filter(field => field.touched)
							.reduce((agg, curr) => {
								agg[curr.name[0]] = curr.touched;
								return agg;
							}, {})
					});
				}}
			>
				<fieldset className="mb-2">
					<legend>Candidate & Job</legend>
					<Form.Item
						label={<label className="ta-input-label">Candidate</label>}
						name="candidate"
						rules={[{ required: true, message: 'Please choose a candidate to recommend' }]}
						initialValue={
							props.candidate &&
							`${props.candidate.code} | ${props.candidate.firstName} ${props.candidate.lastName}`
						}
					>
						{props.candidate ? null : (
							<Radio.Group
								onChange={e => {
									setCandidateType(e.target.value);
									searchCandidates({ candidateType: e.target.value });
								}}
								value={candidateType}
								className="mb-3"
							>
								<Radio.Button key={CandidateType.TALENTS_ARENA} value={CandidateType.TALENTS_ARENA}>
									Talents Arena
								</Radio.Button>
								<Radio.Button key={CandidateType.ACPC} value={CandidateType.ACPC}>
									ACPC
								</Radio.Button>
							</Radio.Group>
						)}
						<TASelect
							showSearch
							placeholder="Type the candidate code, name or email."
							optionFilterProp="children"
							filterOption={false}
							onSearch={val => searchCandidates({ candidateIdentifier: val, candidateType })}
							style={{ width: '100%' }}
							onChange={setRecommendedCandidate}
							disabled={props.candidate}
						>
							{candidates.slice(0, 50).map(candidate => (
								<TASelect.Option
									value={candidate.id}
								>{`${candidate.code} | ${candidate.firstName} ${candidate.lastName}`}</TASelect.Option>
							))}
						</TASelect>
					</Form.Item>
					<Form.Item
						label={<label className="ta-input-label">Job</label>}
						rules={[{ required: true, message: 'Please choose a job to recommend' }]}
						name="job"
						initialValue={props.job && `${props.job.title} | ${props.job.company.name} `}
					>
						<TASelect
							showSearch
							placeholder="Type the job title or company name"
							optionFilterProp="children"
							onSearch={val => searchJobs({ title: val, status: JobStatus[0] })}
							style={{ width: '100%' }}
							disabled={props.job}
							filterOption={(input, option) =>
								option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
						>
							{jobs
								.filter(job => job.company.verified === true)
								.map(job => (
									<TASelect.Option
										value={job.id}
									>{`${job.title} | ${job.company.name} `}</TASelect.Option>
								))}
						</TASelect>
					</Form.Item>
				</fieldset>

				{selectedCandidate && (!selectedCandidate.isRegistered || props.application || props.forceApply) ? (
					<>
						<fieldset className="mb-2">
							<legend>Current Salary</legend>
							<div className="d-flex justify-content-between">
								<Form.Item
									label={<label className="ta-input-label">Amount</label>}
									wrapperCol={{ span: 23 }}
									className="d-inline-block w-100"
									name={['currentSalary', 'value']}
									initialValue={
										props.application?.currentSalary?.value >= 0
											? props.application.currentSalary.value
											: selectedCandidate.currentSalary?.value >= 0
											? selectedCandidate.currentSalary.value
											: undefined
									}
								>
									<TAInputNumber min={0} />
								</Form.Item>
								<Form.Item
									label={<label className="ta-input-label">Currency</label>}
									wrapperCol={{ span: 23 }}
									className="d-inline-block w-100"
									name={['currentSalary', 'currency']}
									initialValue={
										props.application?.currentSalary?.currency ||
										selectedCandidate.currentSalary?.currency ||
										'EGP'
									}
									rules={[
										{
											validator: (rule, value, callback) => {
												if (form.getFieldValue(['currentSalary', 'value']) && !value) {
													callback('Must select a currency');
												}
												callback();
											}
										}
									]}
								>
									<TASelect showSearch placeholder="currency">
										{Object.keys(currencies).map(currency => (
											<TASelect.Option value={currency}>{currency}</TASelect.Option>
										))}
									</TASelect>
								</Form.Item>
								<Form.Item
									label={<label className="ta-input-label">Frequency</label>}
									wrapperCol={{ span: 23 }}
									className="d-inline-block w-100"
									name={['currentSalary', 'frequency']}
									initialValue={
										props.application?.currentSalary?.frequency ||
										selectedCandidate.currentSalary?.frequency ||
										SalaryFrequency.PER_MONTH
									}
									rules={[
										{
											validator: (rule, value, callback) => {
												if (form.getFieldValue(['currentSalary', 'value']) && !value) {
													callback('Must select a frequency');
												}
												callback();
											}
										}
									]}
								>
									<TASelect showSearch placeholder="every...">
										{Object.values(SalaryFrequency).map(frequency => (
											<TASelect.Option value={frequency}>
												{frequency[0].toUpperCase() + frequency.slice(1).replace('-', ' ')}
											</TASelect.Option>
										))}
									</TASelect>
								</Form.Item>
							</div>
							<Form.Item
								name={['currentSalaryVisible']}
								valuePropName="checked"
								initialValue={props.application?.currentSalaryVisible || false}
								style={{ marginTop: '-1rem' }}
							>
								<Checkbox>Show to company</Checkbox>
							</Form.Item>

							<legend>Expected Salary</legend>
							<div className="d-flex justify-content-between">
								<Form.Item
									label={<label className="ta-input-label">Min</label>}
									wrapperCol={{ span: 23 }}
									className="d-inline-block w-100"
									name={['expectedSalary', 'min']}
									initialValue={
										props.application
											? props.application.expectedSalary?.min >= 0
												? props.application.expectedSalary.min
												: undefined
											: selectedCandidate.minimumSalary?.min >= 0
											? selectedCandidate.minimumSalary.min
											: undefined
									}
									rules={[{ required: false }]}
								>
									<TAInputNumber min={0} placeholder="min amount" />
								</Form.Item>
								<Form.Item
									label={<label className="ta-input-label">Max</label>}
									wrapperCol={{ span: 23 }}
									className="d-inline-block w-100"
									name={['expectedSalary', 'max']}
									initialValue={
										props.application
											? props.application.expectedSalary?.max >= 0
												? props.application.expectedSalary.max
												: undefined
											: selectedCandidate.minimumSalary?.max >= 0
											? selectedCandidate.minimumSalary.max
											: undefined
									}
									rules={[
										{ required: false }
										// {
										// 	validator: (rule, value, callback) => {
										// 		if (
										// 			form.getFieldValue(['expectedSalary', 'min']) &&
										// 			value < form.getFieldValue(['expectedSalary', 'min'])
										// 		) {
										// 			callback("Can't be less than minimum value!");
										// 		}
										// 		callback();
										// 	}
										// }
									]}
								>
									<TAInputNumber min={0} placeholder="max amount" />
								</Form.Item>
								<Form.Item
									label={<label className="ta-input-label">Currency</label>}
									wrapperCol={{ span: 23 }}
									className="d-inline-block w-100"
									name={['expectedSalary', 'currency']}
									initialValue={
										props.application
											? props.application.expectedSalary?.currency || 'EGP'
											: selectedCandidate.minimumSalary?.currency || 'EGP'
									}
									rules={[
										{
											required: false,
											validator: (rule, value, callback) => {
												const minOrMax =
													form.getFieldValue(['expectedSalary', 'min']) ||
													form.getFieldValue(['expectedSalary', 'max']);
												if (minOrMax && !value) {
													callback('Must select a currency');
												}
												callback();
											}
										}
									]}
								>
									<TASelect showSearch placeholder="currency">
										{Object.keys(currencies).map(currency => (
											<TASelect.Option value={currency}>{currency}</TASelect.Option>
										))}
									</TASelect>
								</Form.Item>
								<Form.Item
									label={<label className="ta-input-label">Frequency</label>}
									wrapperCol={{ span: 23 }}
									className="d-inline-block w-100"
									name={['expectedSalary', 'frequency']}
									initialValue={
										props.application
											? props.application.expectedSalary?.frequency || SalaryFrequency.PER_MONTH
											: selectedCandidate.minimumSalary?.frequency || SalaryFrequency.PER_MONTH
									}
									rules={[
										{
											required: false,
											validator: (rule, value, callback) => {
												const minOrMax =
													form.getFieldValue(['expectedSalary', 'min']) ||
													form.getFieldValue(['expectedSalary', 'max']);
												if (minOrMax && !value) {
													callback('Must select a frequency');
												}
												callback();
											}
										}
									]}
								>
									<TASelect showSearch placeholder="every...">
										{Object.values(SalaryFrequency).map(frequency => (
											<TASelect.Option value={frequency}>
												{frequency[0].toUpperCase() + frequency.slice(1).replace('-', ' ')}
											</TASelect.Option>
										))}
									</TASelect>
								</Form.Item>
							</div>
							<Form.Item
								name={['expectedSalaryVisible']}
								valuePropName="checked"
								initialValue={props.application?.expectedSalaryVisible === false ? false : true}
								style={{ marginTop: '-1rem' }}
							>
								<Checkbox>Show to company</Checkbox>
							</Form.Item>
							<legend>Notice Period</legend>
							<p style={{ color: 'rgba(0,0,0,0.35)' }}>Note: Zeros mean available immediately</p>
							<div className="d-flex justify-content-between">
								<Form.Item
									label={<label className="ta-input-label">Min</label>}
									wrapperCol={{ span: 23 }}
									className="d-inline-block w-100"
									name={['noticePeriod', 'min']}
									initialValue={
										props.application
											? props.application.noticePeriod?.min >= 0
												? props.application.noticePeriod.min
												: undefined
											: selectedCandidate.noticePeriod?.min >= 0
											? selectedCandidate.noticePeriod.min
											: undefined
									}
									rules={[{ required: false }]}
								>
									<TAInputNumber min={0} />
								</Form.Item>
								<Form.Item
									label={<label className="ta-input-label">Max</label>}
									wrapperCol={{ span: 23 }}
									className="d-inline-block w-100"
									name={['noticePeriod', 'max']}
									initialValue={
										props.application
											? props.application.noticePeriod?.max >= 0
												? props.application.noticePeriod.max
												: undefined
											: selectedCandidate.noticePeriod?.max >= 0
											? selectedCandidate.noticePeriod.max
											: undefined
									}
									rules={[
										{ required: false }
										// {
										// 	validator: (rule, value, callback) => {
										// 		if (
										// 			form.getFieldValue(['noticePeriod', 'min']) &&
										// 			value < form.getFieldValue(['noticePeriod', 'min'])
										// 		) {
										// 			callback("Can't be less than minimum value!");
										// 		}
										// 		callback();
										// 	}
										// }
									]}
								>
									<TAInputNumber min={0} />
								</Form.Item>
								<Form.Item
									label={<label className="ta-input-label">Unit</label>}
									wrapperCol={{ span: 23 }}
									className="d-inline-block w-100"
									name={['noticePeriod', 'unit']}
									initialValue={
										props.application
											? props.application.noticePeriod?.unit || NoticePeriod.MONTH
											: selectedCandidate.noticePeriod?.unit || NoticePeriod.MONTH
									}
									rules={[
										{
											required: false,
											validator: (rule, value, callback) => {
												const minOrMax =
													form.getFieldValue(['noticePeriod', 'min']) ||
													form.getFieldValue(['noticePeriod', 'max']);
												if (minOrMax && !value) {
													callback('Must select unit');
												}
												callback();
											}
										}
									]}
								>
									<TASelect showSearch placeholder="unit">
										{Object.values(NoticePeriod).map(period => (
											<TASelect.Option value={period}>
												{period[0].toUpperCase() + period.slice(1)}
											</TASelect.Option>
										))}
									</TASelect>
								</Form.Item>
							</div>
							<Form.Item
								name={['noticePeriodVisible']}
								valuePropName="checked"
								initialValue={props.application?.noticePeriodVisible === false ? false : true}
								style={{ marginTop: '-1rem' }}
							>
								<Checkbox>Show to company</Checkbox>
							</Form.Item>
						</fieldset>
						<fieldset>
							<legend>Report Info</legend>
							{technicalReports.length ? (
								<Form.Item
									className="mb-1"
									label={<label className="ta-input-label">Technical Report</label>}
									name="technicalReport"
									initialValue={
										props.application?.technicalReports?.length
											? props.application.technicalReports[0].name
											: selectedCandidate?.technicalReport
											? 'Default Report'
											: null
									}
								>
									<TASelect
										showSearch
										placeholder="Select the technical report you want to show for this company"
										style={{ width: '100%' }}
										onChange={report => {
											setSelectedReport(technicalReports.find(r => r.url === report));
										}}
									>
										{technicalReports.map(report => (
											<TASelect.Option value={report.url}>{report.name}</TASelect.Option>
										))}
									</TASelect>
								</Form.Item>
							) : (
								<p>Candidate has no technical reports!</p>
							)}

							{selectedReport && (
								<a className="me-3" href="" onClick={viewReportHandler}>
									View Report
								</a>
							)}

							<a href="" onClick={newReportHandler}>
								{`Add ${technicalReports.length ? 'another' : 'new'} report`}
							</a>
						</fieldset>

						{/* {selectedCandidate &&
						(!selectedCandidate.isRegistered || props.application || props.forceApply) ? (
							<Form.Item
								name={['cvDownloadable']}
								valuePropName="checked"
								initialValue={props.application?.cvDownloadable === false ? false : true}
								className="mt-2"
							>
								<Checkbox>Allow company to download cv at interview</Checkbox>
							</Form.Item>
						) : null} */}
						{selectedReport ? (
							<PDFViewer
								visible={viewReportModalVisible}
								url={selectedReport?.url}
								onCancel={() => setViewReportModalVisible(false)}
							/>
						) : null}

						<AddReportFormModal
							visible={newReportModalVisible}
							onCancel={() => setNewReportModalVisible(false)}
							onAdd={addReportHandler}
							candidateId={selectedCandidate.id}
						/>
					</>
				) : null}
			</Form>
		</Modal>
	);
};
